import type {
  GestureResponderEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  PointerEvent,
} from 'react-native'
import { useCallback, useRef, useState } from 'react'
import { Platform } from 'react-native'

const DEFAULT_PULL_THRESHOLD = 72
const MAX_PULL_DISTANCE = 128

type UseWebPullToRefreshParams = {
  onRefresh: () => Promise<void> | void
  refreshing: boolean
  threshold?: number
}

export function useWebPullToRefresh({
  onRefresh,
  refreshing,
  threshold = DEFAULT_PULL_THRESHOLD,
}: UseWebPullToRefreshParams) {
  const isEnabled = Platform.OS === 'web'
  const startYRef = useRef<number | null>(null)
  const isAtTopRef = useRef(true)
  const [pullDistance, setPullDistance] = useState(0)

  const resetPull = useCallback(() => {
    startYRef.current = null
    setPullDistance(0)
  }, [])

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (!isEnabled) return

      isAtTopRef.current = event.nativeEvent.contentOffset.y <= 0
    },
    [isEnabled],
  )

  const handleTouchStart = useCallback(
    (event: GestureResponderEvent) => {
      if (!isEnabled || refreshing || !isAtTopRef.current) return

      startYRef.current = event.nativeEvent.pageY
      setPullDistance(0)
    },
    [isEnabled, refreshing],
  )

  const handleTouchMove = useCallback(
    (event: GestureResponderEvent) => {
      if (
        !isEnabled ||
        refreshing ||
        startYRef.current == null ||
        !isAtTopRef.current
      ) {
        return
      }

      const deltaY = event.nativeEvent.pageY - startYRef.current
      if (deltaY <= 0) {
        setPullDistance(0)
        return
      }

      setPullDistance(Math.min(deltaY, MAX_PULL_DISTANCE))
    },
    [isEnabled, refreshing],
  )

  const handleTouchEnd = useCallback(
    (_event: GestureResponderEvent) => {
      if (!isEnabled) return

      const shouldRefresh =
        isAtTopRef.current && !refreshing && pullDistance >= threshold

      resetPull()

      if (shouldRefresh) {
        void onRefresh()
      }
    },
    [isEnabled, onRefresh, pullDistance, refreshing, resetPull, threshold],
  )

  const handlePointerDown = useCallback(
    (event: PointerEvent) => {
      if (
        !isEnabled ||
        refreshing ||
        !isAtTopRef.current ||
        event.nativeEvent.pointerType === 'touch'
      ) {
        return
      }

      startYRef.current = event.nativeEvent.pageY
      setPullDistance(0)
    },
    [isEnabled, refreshing],
  )

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      if (
        !isEnabled ||
        refreshing ||
        event.nativeEvent.pointerType === 'touch' ||
        startYRef.current == null ||
        !isAtTopRef.current
      ) {
        return
      }

      const deltaY = event.nativeEvent.pageY - startYRef.current
      if (deltaY <= 0) {
        setPullDistance(0)
        return
      }

      setPullDistance(Math.min(deltaY, MAX_PULL_DISTANCE))
    },
    [isEnabled, refreshing],
  )

  const handlePointerUp = useCallback(
    (event: PointerEvent) => {
      if (
        !isEnabled ||
        refreshing ||
        event.nativeEvent.pointerType === 'touch'
      ) {
        return
      }

      const shouldRefresh = isAtTopRef.current && pullDistance >= threshold

      resetPull()

      if (shouldRefresh) {
        void onRefresh()
      }
    },
    [isEnabled, onRefresh, pullDistance, refreshing, resetPull, threshold],
  )

  return {
    isEnabled,
    pullDistance,
    shouldRelease: pullDistance >= threshold,
    handleScroll,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
  }
}
